import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const pricingPlans = [
    {
      name: 'Starter',
      description: 'Perfect for small businesses starting their AI journey',
      monthlyPrice: 5000,
      yearlyPrice: 50000,
      features: [
        'AI Strategy Consultation',
        'Proof of Concept Development',
        'Basic AI Integration',
        '2-week delivery timeline',
        'Email support',
        '3 months warranty'
      ],
      limitations: [
        'Limited to 1 AI model',
        'Basic reporting only',
        'No custom training'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Professional',
      description: 'Ideal for growing companies with established AI needs',
      monthlyPrice: 15000,
      yearlyPrice: 150000,
      features: [
        'Everything in Starter',
        'Custom AI Model Development',
        'Advanced Analytics & Reporting',
        'API Integration',
        'Priority support',
        '6 months warranty',
        'Performance monitoring',
        'Monthly optimization'
      ],
      limitations: [
        'Up to 3 AI models',
        'Standard security features'
      ],
      cta: 'Most Popular',
      popular: true
    },
    {
      name: 'Enterprise',
      description: 'For large organizations requiring comprehensive AI solutions',
      monthlyPrice: 50000,
      yearlyPrice: 500000,
      features: [
        'Everything in Professional',
        'Unlimited AI Models',
        'Custom Infrastructure Setup',
        'Advanced Security & Compliance',
        'Dedicated AI Engineer',
        '24/7 support',
        '1 year warranty',
        'Quarterly strategy reviews',
        'Custom integrations',
        'Training & workshops'
      ],
      limitations: [
        'Annual commitment required'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  const engagementModels = [
    {
      name: 'Fixed-Price Projects',
      icon: 'bi-currency-dollar',
      description: 'Predictable pricing for well-defined projects with clear deliverables.',
      pros: [
        'Fixed budget and timeline',
        'Clear deliverables',
        'No surprise costs',
        'Suitable for MVP development'
      ],
      cons: [
        'Less flexibility for changes',
        'Requires detailed upfront planning',
        'May not suit evolving requirements'
      ],
      bestFor: 'Well-defined projects with clear requirements',
      pricing: 'Starting from $25,000'
    },
    {
      name: 'Time & Materials',
      icon: 'bi-clock',
      description: 'Flexible engagement model based on actual time spent and resources used.',
      pros: [
        'Maximum flexibility',
        'Easy to scale up/down',
        'Transparent billing',
        'Adapt to changing requirements'
      ],
      cons: [
        'Less predictable costs',
        'Requires active management',
        'May exceed initial estimates'
      ],
      bestFor: 'Projects with evolving requirements or ongoing development',
      pricing: 'Starting from $150/hour'
    },
    {
      name: 'Dedicated Teams',
      icon: 'bi-people',
      description: 'Exclusive team of AI professionals working exclusively on your projects.',
      pros: [
        'Full team dedication',
        'Deep domain knowledge',
        'Consistent quality',
        'Long-term partnership'
      ],
      cons: [
        'Higher upfront investment',
        'Requires ongoing management',
        'Minimum commitment period'
      ],
      bestFor: 'Long-term projects or ongoing AI development needs',
      pricing: 'Starting from $25,000/month'
    }
  ];

  const addOnServices = [
    {
      name: 'AI Training & Workshops',
      description: 'Educate your team on AI implementation and best practices',
      price: '$5,000/day',
      features: ['Custom curriculum', 'Hands-on exercises', 'Follow-up support']
    },
    {
      name: 'AI Infrastructure Setup',
      description: 'Complete cloud infrastructure setup for AI workloads',
      price: '$15,000',
      features: ['AWS/Azure/GCP setup', 'Security configuration', 'Monitoring tools']
    },
    {
      name: 'Data Migration & Integration',
      description: 'Seamless integration with your existing systems and data',
      price: '$10,000',
      features: ['Data pipeline setup', 'API development', 'Testing & validation']
    },
    {
      name: 'Ongoing Maintenance',
      description: 'Continuous monitoring, updates, and optimization',
      price: '$3,000/month',
      features: ['24/7 monitoring', 'Performance optimization', 'Security updates']
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pricing-hero-section">
        <div className="hero-background" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80')` }}>
          <div className="container">
            <div className="row align-items-center min-vh-80">
              <div className="col-lg-8 mx-auto text-center text-white">
                <h1 className="hero-title mb-4">Transparent Pricing & Engagement Models</h1>
                <p className="hero-subtitle mb-5">Choose the right engagement model for your AI transformation journey. No hidden costs, no surprises.</p>
                <div className="hero-stats">
                  <div className="row g-4">
                    <div className="col-6 col-md-3">
                      <div className="stat-card">
                        <div className="stat-number">3</div>
                        <div className="stat-label">Engagement Models</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="stat-card">
                        <div className="stat-number">95%</div>
                        <div className="stat-label">On-Time Delivery</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="stat-card">
                        <div className="stat-number">24/7</div>
                        <div className="stat-label">Support Available</div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div className="stat-card">
                        <div className="stat-number">100%</div>
                        <div className="stat-label">Transparency</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="section-title">Engagement Models</h2>
                <p className="section-subtitle">Choose the model that best fits your project needs and budget</p>
              </div>
              <div className="row g-4">
                {engagementModels.map((model, index) => (
                  <div key={index} className="col-md-4">
                    <div className="engagement-model-card">
                      <div className="model-icon">
                        <i className={model.icon}></i>
                      </div>
                      <h3>{model.name}</h3>
                      <p className="model-description">{model.description}</p>
                      <div className="model-pricing">{model.pricing}</div>
                      
                      <div className="model-pros">
                        <h6>Advantages:</h6>
                        <ul>
                          {model.pros.map((pro, proIndex) => (
                            <li key={proIndex}>{pro}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="model-cons">
                        <h6>Considerations:</h6>
                        <ul>
                          {model.cons.map((con, conIndex) => (
                            <li key={conIndex}>{con}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="model-best-for">
                        <strong>Best for:</strong> {model.bestFor}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="section-title">Service Packages</h2>
                <p className="section-subtitle">Comprehensive AI solutions tailored to your business size and needs</p>
                
                {/* Billing Toggle */}
                <div className="billing-toggle mb-4">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${billingCycle === 'monthly' ? 'btn-gradient-primary' : 'btn-outline-gradient-primary'}`}
                      onClick={() => setBillingCycle('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={`btn ${billingCycle === 'yearly' ? 'btn-gradient-primary' : 'btn-outline-gradient-primary'}`}
                      onClick={() => setBillingCycle('yearly')}
                    >
                      Yearly <span className="badge bg-success ms-1">Save 17%</span>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="row g-4">
                {pricingPlans.map((plan, index) => (
                  <div key={index} className="col-md-4">
                    <div className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                      {plan.popular && <div className="popular-badge">Most Popular</div>}
                      
                      <div className="pricing-header">
                        <h3>{plan.name}</h3>
                        <p className="plan-description">{plan.description}</p>
                        <div className="pricing-amount">
                          <span className="currency">$</span>
                          <span className="amount">
                            {billingCycle === 'monthly' ? plan.monthlyPrice.toLocaleString() : plan.yearlyPrice.toLocaleString()}
                          </span>
                          <span className="period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                        </div>
                      </div>
                      
                      <div className="pricing-features">
                        <h6>What's Included:</h6>
                        <ul>
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex}>
                              <i className="bi bi-check-circle text-success"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {plan.limitations.length > 0 && (
                        <div className="pricing-limitations">
                          <h6>Limitations:</h6>
                          <ul>
                            {plan.limitations.map((limitation, limitationIndex) => (
                              <li key={limitationIndex}>
                                <i className="bi bi-exclamation-circle text-warning"></i>
                                {limitation}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      <div className="pricing-cta">
                        <Link 
                          to="/#contact" 
                          className={`btn ${plan.popular ? 'btn-gradient-primary' : 'btn-outline-gradient-primary'} btn-lg w-100`}
                        >
                          {plan.cta}
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add-on Services */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="text-center mb-5">
                <h2 className="section-title">Additional Services</h2>
                <p className="section-subtitle">Enhance your AI implementation with these specialized services</p>
              </div>
              <div className="row g-4">
                {addOnServices.map((service, index) => (
                  <div key={index} className="col-md-6">
                    <div className="addon-service-card">
                      <div className="addon-header">
                        <h4>{service.name}</h4>
                        <div className="addon-price">{service.price}</div>
                      </div>
                      <p className="addon-description">{service.description}</p>
                      <div className="addon-features">
                        <ul>
                          {service.features.map((feature, featureIndex) => (
                            <li key={featureIndex}>
                              <i className="bi bi-check text-success"></i>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="text-center mb-5">
                <h2 className="section-title">Frequently Asked Questions</h2>
                <p className="section-subtitle">Common questions about our pricing and engagement models</p>
              </div>
              
              <div className="faq-section">
                <div className="faq-item">
                  <h5>What's included in the pricing?</h5>
                  <p>Our pricing includes development, testing, deployment, and initial support. Additional services like training, infrastructure setup, and ongoing maintenance are available as add-ons.</p>
                </div>
                
                <div className="faq-item">
                  <h5>Can I switch between engagement models?</h5>
                  <p>Yes, you can switch between engagement models based on your evolving needs. We'll work with you to ensure a smooth transition.</p>
                </div>
                
                <div className="faq-item">
                  <h5>Do you offer discounts for long-term contracts?</h5>
                  <p>Yes, we offer volume discounts for annual contracts and enterprise clients. Contact us for custom pricing.</p>
                </div>
                
                <div className="faq-item">
                  <h5>What about intellectual property ownership?</h5>
                  <p>You retain full ownership of your data and any custom AI models we develop for you. We only retain rights to our proprietary frameworks and tools.</p>
                </div>
                
                <div className="faq-item">
                  <h5>How do you handle project changes and scope creep?</h5>
                  <p>We use agile methodologies and maintain transparent communication. Changes are documented and priced accordingly, with your approval before implementation.</p>
                </div>
                
                <div className="faq-item">
                  <h5>What support is included after project completion?</h5>
                  <p>All plans include post-launch support and warranty periods. We also offer ongoing maintenance and optimization services.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-gradient-primary text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="mb-4">Ready to Get Started?</h2>
              <p className="mb-4">Let's discuss your project requirements and find the perfect engagement model for your AI transformation journey.</p>
              <div className="cta-buttons">
                <Link to="/#contact" className="btn btn-light btn-lg me-3">Get a Custom Quote</Link>
                <Link to="/case-studies" className="btn btn-outline-light btn-lg">View Case Studies</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingPage; 